import {
    Children,
    ComponentPropsWithRef,
    ElementType,
    forwardRef,
    MouseEvent,
    ReactNode,
    useCallback,
    useRef,
    useState,
} from 'react'
import { Popover, PopoverButton, PopoverBackdrop, PopoverPanel } from '@headlessui/react'
import classNames from 'classnames'

import { Spinner } from '@authy/lib-client/components'

import { Menu } from './Menu'
import { DropdownArrow } from './types'
import { AutoFocusHandler } from './AutoFocusHandler'

export type ButtonVariant = 'default' | 'flat' | 'success' | 'danger'

type ButtonGroupParts = {
    top?: boolean
    bottom?: boolean
    left?: boolean
    right?: boolean
}

type ButtonActionProps =
    | {
          onClick?: (evt: MouseEvent<HTMLButtonElement>) => void | Promise<any>
          popupContent?: never
          noPopupFocus?: never
          menuItems?: never
          dropdownArrow?: never
      }
    | {
          onClick?: never
          popupContent?: (close: () => void) => ReactNode
          noPopupFocus?: boolean
          menuItems?: ReactNode
          dropdownArrow?: DropdownArrow
      }

export type ButtonProps = ButtonActionProps & {
    Icon?: ElementType<ComponentPropsWithRef<'svg'>>
    variant?: ButtonVariant
    small?: boolean
    block?: boolean
    labelPosition?: 'default' | 'block' | 'bottom' | 'responsive'
    loading?: boolean
    disabled?: boolean
    className?: string
    title?: string
    groupParts?: ButtonGroupParts
    children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        Icon,
        variant = 'default',
        small,
        block,
        labelPosition,
        loading,
        disabled,
        className,
        title,
        onClick,
        popupContent,
        noPopupFocus,
        menuItems,
        dropdownArrow = 'default',
        groupParts,
        children,
        ...props
    },
    ref
) {
    const [internalLoading, setInternalLoading] = useState<boolean>(false)
    const popoverPanelRef = useRef<HTMLElement>(null)

    const handleClick = useCallback(
        async (evt: MouseEvent<HTMLButtonElement>) => {
            if (onClick) {
                const result = onClick(evt)
                if (result instanceof Promise) {
                    try {
                        setInternalLoading(true)
                        await result
                    } finally {
                        setInternalLoading(false)
                    }
                }
            }
        },
        [onClick]
    )

    const handleClickStop = useCallback((evt: MouseEvent<HTMLButtonElement>) => {
        evt.stopPropagation()
    }, [])

    const isLoading = loading || internalLoading

    const baseProps = {
        disabled: disabled || isLoading,
        title,
    }

    const hasIcon = !!Icon
    const hasLabel = Children.count(children) > 0

    const baseClassName = classNames(
        className,
        'relative justify-center items-center gap-2 overflow-hidden select-none active:outline-hidden dark:active:outline-hidden rounded-md text-left',
        {
            'flex-col': labelPosition === 'bottom',
            'inline-flex': !block,
            'flex w-full': block,
            'p-1.5': hasIcon && !hasLabel && small,
            'px-2 py-1.5': hasLabel && small,
            'p-2.5': hasIcon && !hasLabel && !small,
            'px-4 py-2.5': hasLabel && !small,
            'text-xs font-medium': small,
            'text-sm font-medium': !small,
            'focus-outline': variant !== 'flat',
            'btn-default': variant === 'default',
            'btn-flat': variant === 'flat',
            'btn-success': variant === 'success',
            'btn-danger': variant === 'danger',
            'cursor-default': !disabled,
            'cursor-not-allowed opacity-60 dark:opacity-40': disabled,
            'cursor-wait': isLoading,
        },
        groupParts && {
            'shadow-none': true,
            // -- vertical
            '!rounded-t-none': !groupParts.top,
            '!rounded-b-none': !groupParts.bottom,
            // -- horizontal
            '!rounded-l-none': !groupParts.left,
            '!rounded-r-none': !groupParts.right,
        }
    )

    const content = (
        <>
            {isLoading && (
                <span className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center">
                    <Spinner
                        className={classNames('shrink-0', {
                            'h-4 w-4': small,
                            'h-7 w-7': !small,
                        })}
                    />
                </span>
            )}
            {hasIcon && (
                <Icon
                    className={classNames('icon shrink-0', {
                        'size-4': small,
                        'size-5': !small,
                        'opacity-0': isLoading,
                    })}
                />
            )}
            {hasLabel && (
                <span
                    className={classNames('block overflow-hidden text-ellipsis', {
                        'justify-center text-center': labelPosition === 'bottom',
                        'opacity-0': isLoading,
                        'sm:hidden': labelPosition === 'responsive',
                        'flex-1': labelPosition === 'block',
                    })}
                >
                    {children}
                </span>
            )}
        </>
    )

    if (popupContent || menuItems) {
        const className = classNames(baseClassName, {
            'dropdown-arrow': dropdownArrow !== 'none',
            'dropdown-arrow-invert': dropdownArrow !== 'none' && !['default', 'flat'].includes(variant),
            'dropdown-arrow-padding': !block && dropdownArrow !== 'none',
            '[&:not(:hover)]:bg-none': dropdownArrow === 'hover',
        })

        if (popupContent) {
            return (
                <Popover className={classNames('relative', { 'inline-block': !block, 'block w-full': block })}>
                    {({ open, close }) => (
                        <>
                            <PopoverButton
                                {...baseProps}
                                className={classNames(className, { active: open })}
                                onClick={handleClickStop}
                            >
                                {content}
                            </PopoverButton>
                            <PopoverBackdrop className="fixed inset-0 z-10 bg-black/0" />
                            <PopoverPanel
                                className="popup z-20"
                                anchor="bottom start"
                                portal
                                focus={!noPopupFocus}
                                ref={popoverPanelRef}
                            >
                                {open && (
                                    <AutoFocusHandler containerRef={popoverPanelRef}>
                                        {popupContent(close)}
                                    </AutoFocusHandler>
                                )}
                            </PopoverPanel>
                        </>
                    )}
                </Popover>
            )
        } else if (menuItems) {
            return (
                <Menu as="div" className={classNames('relative', { 'inline-block': !block, 'block w-full': block })}>
                    {({ open }) => (
                        <>
                            <Menu.Button
                                {...baseProps}
                                className={classNames(className, { active: open })}
                                onClick={handleClickStop}
                            >
                                {content}
                            </Menu.Button>
                            {open && (
                                <Menu.Items static portal anchor="bottom start" onClick={handleClickStop}>
                                    {menuItems}
                                </Menu.Items>
                            )}
                        </>
                    )}
                </Menu>
            )
        }
    }

    return (
        <button {...props} ref={ref} {...baseProps} className={baseClassName} type="button" onClick={handleClick}>
            {content}
        </button>
    )
})
